<template>
  <div class="row mx-0">
    <div class="col-1"></div>
    <div class="col-10">
      <div class="p-0 m-0 mt-4 main-title">稼動率統計</div>
      <div style="margin-top: 2rem">
        <commonQueryInput
          :inputItems="multiInputItems"
          :immediateInputValues="true"
          v-model:getValue="multiInputVal"
          v-model:getImmediateValue="immediateMultiInputVal"
        ></commonQueryInput>
      </div>
      <div style="margin-top: 2rem">
        <queryResultTable :queryResultTableItems="queryResultTableItems" />
      </div>
      <div style="margin-top: 2rem">
        <radioButtonChart
          :chartArray="chartData"
          :radioArray="['依日統計', '依周統計', '依月統計']"
        />
      </div>
      <div class="text-end" style="margin-bottom: 0.6rem">
        <exportCsv
          :exportData="activationItems"
          :exportDataTitle="activationFields.map((item) => item.label)"
          :exportFileName="exportFileName()"
        />
      </div>
      <commonTable
        :fields="activationFields"
        :items="activationItems"
        :theme="'green'"
        stickyHeader="230px"
      />
    </div>
    <div class="col-1"></div>
  </div>
</template>

<script>
import commonQueryInput from "@/components/commonQueryInput.vue";
import queryResultTable from "@/components/queryResultTable.vue";
import radioButtonChart from "@/components/radioButtonChart.vue";
import exportCsv from "@/components/exportCsv.vue";
import commonTable from "@/components/commonTable.vue";
import { computed, inject, ref, watch } from "vue";
import { useStore } from "vuex";
export default {
  components: {
    commonQueryInput,
    queryResultTable,
    radioButtonChart,
    exportCsv,
    commonTable,
  },
  setup() {
    const store = useStore();

    //getters
    const loginData = computed(() => store.getters["getLogin/get_loginData"]);
    const sideNavPath = computed(
      () => store.getters["sideBarTree/get_sideNavPath"]
    );
    const axiosResult = computed(
      () => store.getters["commonAxios/get_axiosResult"]
    );
    const oper = computed(() => store.getters["selectOptions/get_oper"]);
    const device = computed(() => store.getters["selectOptions/get_device"]);

    //dispatch
    const methods = {
      axiosAction: async (payload) => {
        await store.dispatch("commonAxios/axiosAction", payload);
      },
      triggerLoadingBar: () => {
        store.dispatch("commonLoadingBar/triggerLoadingBar");
      },
      setTimeoutAlert: (alertText) => {
        store.dispatch("commonAlert/setTimeoutAlert", [alertText]);
      },
      set_device: (payload) => {
        store.dispatch("selectOptions/set_device", payload);
      },
    };

    const path = inject("path");
    const multiInputVal = ref();
    const multiInputItems = ref([
      {
        key: "date",
        label: "日期",
        type: "dateBetween", // date/dateBetween/text/select/
      },
      {
        key: "oper",
        label: "製程",
        type: "select",
        checkEmpty: true,
        disableText: "-- 選擇製程 --",
        options: oper,
      },
      {
        key: "deviceID",
        label: "機台",
        type: "select",
        bindKey: "oper",
        checkEmpty: true,
        disableText: "-- 選擇機台 --",
        bindOptions: device,
      },
    ]);
    const immediateMultiInputVal = ref();

    //查詢回來的機台名稱
    const deviceName = ref("");

    //查詢結果表格資料
    const queryResultTableItems = ref([]);

    //圖表X軸資料
    const chartXaxisData = ref({ day: [], week: [], month: [] });
    //圖表Y軸資料
    const chartYaxisData = ref({ day: [], week: [], month: [] });

    //圖表資料
    const chartData = ref([
      {
        // 依日統計圖表
        chartType: "customize",
        chartData: {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              label: {
                backgroundColor: "#6a7985",
              },
            },
          },
          legend: {
            data: [deviceName.value, "平均稼動率"],
            bottom: 0,
          },
          grid: {
            top: "20%",
            left: "3%",
            right: "4%",
            bottom: "12%",
            containLabel: true,
          },
          xAxis: [
            {
              type: "category",
              data: chartXaxisData.day,
              splitLine: { show: false },
              axisPointer: {
                type: "shadow",
              },
            },
          ],
          yAxis: {
            type: "value",
            name: "單位 %",
            minInterval: 1,
          },
          series: [
            {
              data: chartYaxisData.day,
              name: deviceName.value,
              type: "bar",
              itemStyle: {
                color: "#4b8c80",
              },
            },
            {
              data: chartYaxisData.day,
              name: "平均稼動率",
              type: "line",
              smooth: true,
              itemStyle: {
                color: "#000000",
              },
            },
          ],
        },
      },
      {
        // 依周統計圖表
        chartType: "customize",
        chartData: {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              label: {
                backgroundColor: "#6a7985",
              },
            },
          },
          legend: {
            data: [deviceName.value, "平均稼動率"],
            bottom: 0,
          },
          grid: {
            top: "20%",
            left: "3%",
            right: "4%",
            bottom: "12%",
            containLabel: true,
          },
          xAxis: [
            {
              type: "category",
              data: chartXaxisData.week,
              splitLine: { show: false },
              axisPointer: {
                type: "shadow",
              },
            },
          ],
          yAxis: {
            type: "value",
            name: "單位 %",
            minInterval: 1,
          },
          series: [
            {
              data: chartYaxisData.week,
              name: deviceName.value,
              type: "bar",
              itemStyle: {
                color: "#4b8c80",
              },
            },
            {
              data: chartYaxisData.week,
              name: "平均稼動率",
              type: "line",
              smooth: true,
              itemStyle: {
                color: "#000000",
              },
            },
          ],
        },
      },
      {
        // 依日統計圖表
        chartType: "customize",
        chartData: {
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              label: {
                backgroundColor: "#6a7985",
              },
            },
          },
          legend: {
            data: [deviceName.value, "平均稼動率"],
            bottom: 0,
          },
          grid: {
            top: "20%",
            left: "3%",
            right: "4%",
            bottom: "12%",
            containLabel: true,
          },
          xAxis: [
            {
              type: "category",
              data: chartXaxisData.month,
              splitLine: { show: false },
              axisPointer: {
                type: "shadow",
              },
            },
          ],
          yAxis: {
            type: "value",
            name: "單位 %",
            minInterval: 1,
          },
          series: [
            {
              data: chartYaxisData.month,
              name: deviceName.value,
              type: "bar",
              itemStyle: {
                color: "#4b8c80",
              },
            },
            {
              data: chartYaxisData.month,
              name: "平均稼動率",
              type: "line",
              smooth: true,
              itemStyle: {
                color: "#000000",
              },
            },
          ],
        },
      },
    ]);

    //稼動率資訊
    const activationFields = [
      {
        key: "date",
        label: "日期",
        sortable: true,
        thStyle: {
          "min-width": "125px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
      {
        key: "activation",
        label: "稼動率",
        sortable: true,
        thStyle: {
          "min-width": "125px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
    ];
    const activationItems = ref([]);

    //匯出資料的檔案名稱
    const exportFileName = () => {
      let exportFileName = "";
      if (deviceName.value != "") {
        exportFileName =
          "稼動率_" +
          deviceName.value +
          "_" +
          new Date().getFullYear() +
          ("0" + (new Date().getMonth() + 1)).slice(-2) +
          ("0" + new Date().getDate()).slice(-2);
      }
      return exportFileName;
    };

    //watch 按查詢鍵回傳所有輸入框的值
    watch(
      multiInputVal,
      (newValue) => {
        methods.triggerLoadingBar();
        let params = {};
        params["webPage"] =
          sideNavPath.value.length != 0
            ? sideNavPath.value[sideNavPath.value.length - 1]
            : null;
        params["user"] = loginData.value.account;
        params["vuePage"] = path.value;
        console.log(path.value);
        params["methods"] = "POST";
        params["whichFunction"] = "MainQuery";
        params["startTime"] = newValue["date"]["startDate"] + " 00:00:00";
        params["endTime"] = newValue["date"]["endDate"] + " 23:59:59";
        params["oper"] = newValue["oper"];
        params["deviceID"] = newValue["deviceID"];

        methods.axiosAction(params).then(() => {
          console.log(axiosResult.value);
          let result = axiosResult.value;
          let response = result.Response;
          if (response == "ok") {
            let queryTableData = result.QueryTableData;
            deviceName.value = queryTableData[0].query_date[5];
            queryResultTableItems.value = [
              {
                date:
                  queryTableData[0].query_date[0].split(" ")[0] +
                  "~" +
                  queryTableData[0].query_date[1].split(" ")[0],
                site: queryTableData[0].query_date[2],
                oper: queryTableData[0].query_date[3],
                group: queryTableData[0].query_date[4],
                device: deviceName.value,
              },
            ];
            chartXaxisData.value = {
              day: queryTableData[0].device_data_day.data_time,
              week: queryTableData[0].device_data_week.data_time,
              month: queryTableData[0].device_data_month.data_time,
            };
            chartYaxisData.value = {
              day: queryTableData[0].device_data_day.data[deviceName.value]
                .data,
              week: queryTableData[0].device_data_week.data[deviceName.value]
                .data,
              month:
                queryTableData[0].device_data_month.data[deviceName.value].data,
            };
            activationItems.value = queryTableData[0].device_detail_data;
          } else {
            deviceName.value = "";
            chartXaxisData.value = { day: [], week: [], month: [] };
            chartYaxisData.value = { day: [], week: [], month: [] };
            activationItems.value = [];
            methods.setTimeoutAlert(response);
          }
          methods.triggerLoadingBar();
        });
      },
      { deep: true }
    );

    const startTime = ref("");
    const process = ref("");
    // 查詢機台
    watch(
      immediateMultiInputVal,
      (newValue) => {
        if (newValue.date == "" || newValue.date == null) {
          if (
            newValue.date.startDate == "" ||
            newValue.date.startDate == null
          ) {
            return;
          }
        }
        if (newValue.oper == "" || newValue.oper == null) {
          return;
        }
        if (
          startTime.value == newValue.date.startDate &&
          process.value == newValue.oper
        ) {
          return;
        }
        let params = {};
        params["user"] = loginData.value.account;
        params["vuePage"] = "selectoption";
        params["methods"] = "GET";
        params["whichFunction"] = "GetDeviceOption";
        if (newValue.date != "" || newValue.date != null) {
          if (
            newValue.date.startDate != "" ||
            newValue.date.startDate != null
          ) {
            params["startTime"] = newValue.date.startDate;
          }
        }
        if (newValue.oper != "" || newValue.oper != null) {
          params["process"] = newValue.oper;
        } else {
          params["process"] = "-";
        }
        methods.axiosAction(params).then(() => {
          let result = axiosResult.value;
          if (result["Response"] == "ok") {
            methods.set_device(result.QueryTableData.device);
            startTime.value = newValue.date.startDate;
            process.value = newValue.oper;
          } else {
            let returnData = {};
            returnData[params.process] = [];
            methods.set_device(returnData);
            startTime.value = "";
            process.value = "";
          }
        });
      },
      { deep: true }
    );

    //更新機台名稱
    watch(deviceName, (newValue) => {
      chartData.value[0].chartData.legend.data[0] = newValue;
      chartData.value[0].chartData.series[0].name = newValue;
      chartData.value[1].chartData.legend.data[0] = newValue;
      chartData.value[1].chartData.series[0].name = newValue;
      chartData.value[2].chartData.legend.data[0] = newValue;
      chartData.value[2].chartData.series[0].name = newValue;
    });
    //更新X軸資料
    watch(
      chartXaxisData,
      (newValue) => {
        chartData.value[0].chartData.xAxis[0].data = newValue.day;
        chartData.value[1].chartData.xAxis[0].data = newValue.week;
        chartData.value[2].chartData.xAxis[0].data = newValue.month;
        console.log(chartData.value[0].chartData.xAxis);
        console.log(chartData.value[1].chartData.xAxis);
        console.log(chartData.value[2].chartData.xAxis);
      },
      { deep: true }
    );
    //更新Y軸資料
    watch(
      chartYaxisData,
      (newValue) => {
        chartData.value[0].chartData.series[0].data = newValue.day;
        chartData.value[0].chartData.series[1].data = newValue.day;
        chartData.value[1].chartData.series[0].data = newValue.week;
        chartData.value[1].chartData.series[1].data = newValue.week;
        chartData.value[2].chartData.series[0].data = newValue.month;
        chartData.value[2].chartData.series[1].data = newValue.month;
      },
      { deep: true }
    );

    return {
      multiInputVal,
      multiInputItems,
      immediateMultiInputVal,
      queryResultTableItems,
      chartData,
      activationFields,
      activationItems,
      exportFileName,
    };
  },
};
</script>

<style></style>
